import React, { useMemo } from 'react';
import { TableBody, TableCell, TableRow, Checkbox, toNumberString } from '@client/toolkit';
import { useTranslation } from 'react-i18next';
import { ExpanableTableWraper } from '../../components/expanable-table-wraper';
import { TableExpandableCell } from '../../components/table-expanable-cell';
import { useGetEosConsumptionByDeviceQuery, EosResolution } from '@client/api';
import { useDashboardFilters } from '../../../dashboard/context/dashboard-filters.context';

export const ConsumptionBuildingsTable: React.FC = () => {
  const { t } = useTranslation('common');
  const { selectedSpaceIds, selectedDateFromIso, selectedDateToIso } = useDashboardFilters();
  const { data } = useGetEosConsumptionByDeviceQuery(
    {
      from: selectedDateFromIso,
      to: selectedDateToIso,
      resolution: 'yearly' as EosResolution,
      spaceIds: selectedSpaceIds,
    },
    {
      skip: !selectedDateFromIso || !selectedDateToIso,
    },
  );

  const columns = useMemo(() => {
    const { spaces } = data ?? {};
    const seriesKeys = spaces?.flatMap((space) =>
      space.devices.flatMap((device) => device.series.map((series) => series.key)),
    );
    const series = Array.from(new Set(seriesKeys)).map((key) => ({
      key: new Date(key).getFullYear().toString(),
      label: `${new Date(key).getFullYear()} (kWh)`,
      isSortable: true,
    }));
    return [
      { key: 'space', label: t('building'), isSortable: true },
      { key: 'devices', label: t('sensors'), isSortable: true },
      { key: 'type', label: 'Type' },
      ...series,
    ];
  }, [data, t]);

  const dataRows = useMemo(() => {
    const rows = data?.spaces || [];
    if (!rows.length) return [];
    const seriesKeys = new Set<string>();
    rows
      .flatMap((space) => space.devices.flatMap((device) => device.series.map((series) => series.key)))
      .forEach((key) => seriesKeys.add(key));
    return rows.map((space) => {
      const series = space.devices
        .flatMap((device) => device.series)
        .reduce((acc, series) => {
          const key = new Date(series.key).getFullYear().toString();
          const prevValue = acc.get(key) || 0;
          const newValue = prevValue + (series.value || 0);
          acc.set(key, newValue);
          return acc;
        }, new Map<string, number>());
      return {
        spaceId: space.id,
        spaceName: space.name,
        deviceCount: space.devices.length,
        series,
        devices: space.devices.map((device) => ({
          id: device.id,
          name: device.name,
          type: device.type,
          series: device.series.map((series) => ({
            key: new Date(series.key).getFullYear().toString(),
            value: series.value,
          })),
        })),
      };
    });
  }, [data]);

  return (
    <ExpanableTableWraper
      columns={columns}
      data={dataRows}
      pageSize={50}
      renderBody={(
        data,
        selectedRows,
        expandedRows,
        handleToggleSelect,
        handleKeyDownSelect,
        handleToggleExpand,
        handleKeyDownExpand,
      ) => {
        return (
          <TableBody>
            {data.map((space) => (
              <React.Fragment key={space.spaceId}>
                <TableRow className="bg-surface-elevated/40 border-b border-surface-elevated group hover:bg-surface-elevated">
                  <TableCell className="px-2 py-2 align-top content-center">
                    <Checkbox
                      checked={selectedRows.has(space.spaceId)}
                      onChange={() => handleToggleSelect(space.spaceId)}
                      tabIndex={0}
                      aria-label={`Velg ${space.spaceName}`}
                      onKeyDown={(e) => handleKeyDownSelect(e, space.spaceId)}
                    />
                  </TableCell>
                  <TableExpandableCell
                    label={space.spaceName}
                    onClick={() => handleToggleExpand(space.spaceId)}
                    onKeyDown={(e) => handleKeyDownExpand(e, space.spaceId)}
                    ariaLabel={expandedRows.has(space.spaceId) ? `Lukk ${space.spaceName}` : `Åpne ${space.spaceName}`}
                    isExpanded={expandedRows.has(space.spaceId)}
                  />
                  <TableCell className="px-2 py-2 align-top">{space.devices.length.toString()}</TableCell>
                  <TableCell className="px-2 py-2 align-top"></TableCell>
                  {columns.slice(3).map((column) => {
                    const value = space.series.get(column.key) ? space.series.get(column.key)?.toFixed(1) : 0;
                    return (
                      <TableCell key={column.key} className="px-2 py-2 align-top">
                        {toNumberString(Number(value))}
                      </TableCell>
                    );
                  })}
                </TableRow>
                {expandedRows.has(space.spaceId) &&
                  space.devices.map((device: any) => (
                    <React.Fragment key={device.id}>
                      <TableRow className="bg-surface-base border-b border-surface-elevated">
                        <TableCell className="px-2 py-2 align-top"></TableCell>
                        <TableCell className="px-2 py-2 align-top"></TableCell>
                        <TableCell className="px-2 py-2 align-top">{device.name}</TableCell>
                        <TableCell className="px-2 py-2 align-top">{device.type}</TableCell>
                        {columns.slice(3).map((column) => {
                          const serie = device.series.find((s: any) => s.key === column.key);
                          return (
                            <TableCell key={column.key} className="px-2 py-2 align-top">
                              {toNumberString(serie?.value ?? 0)}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </React.Fragment>
                  ))}
              </React.Fragment>
            ))}
          </TableBody>
        );
      }}
    />
  );
};
